<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { FileService } from '@/services/file.services';
import MediaDetailDrawer from './MediaDetailDrawer.vue';

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const router = useRouter();

// ── Commerces (pour filtre + upload) ───────────────────────────────────────────
const { data: restaurantsData } = useQuery({
  queryKey: ['mp-restaurants'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
});
const restaurants = computed<any[]>(() => {
  const d = restaurantsData.value;
  const list = d?.data ?? d?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});
function restaurantName(id: string | null | undefined) {
  if (!id) return '—';
  const r = restaurants.value.find((r: any) => r.id === id);
  return r ? (r.designation ?? r.name ?? id) : id;
}

// ── Filtre commerce (dropdown custom) ──────────────────────────────────────────
const storeFilter = ref('');
const storeDropdownOpen = ref(false);
const storeSearch = ref('');
const filteredStores = computed(() =>
  restaurants.value.filter((r) =>
    (r.designation ?? r.name ?? '')
      .toLowerCase()
      .includes(storeSearch.value.toLowerCase()),
  ),
);
function selectStore(id: string) {
  storeFilter.value = id;
  storeDropdownOpen.value = false;
  storeSearch.value = '';
}
function clearStore() {
  storeFilter.value = '';
  storeSearch.value = '';
  storeDropdownOpen.value = false;
}
const selectedStoreName = computed(() =>
  storeFilter.value ? restaurantName(storeFilter.value) : '',
);

// ── Liste médias ─────────────────────────────────────────────────────────────────
const search = ref('');
const page = ref(1);
const PAGE_SIZES = [10, 20, 50, 100] as const;
const pageSize = ref<(typeof PAGE_SIZES)[number]>(20);
const sortOrder = ref<'ASC' | 'DESC'>('DESC');

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'DESC' ? 'ASC' : 'DESC';
}

watch([search, storeFilter, pageSize, sortOrder], () => {
  page.value = 1;
});

const {
  data: filesData,
  isPending: filesLoading,
  isError: filesError,
  error: filesErrorObj,
} = useQuery({
  queryKey: computed(() => [
    'mp-files',
    page.value,
    pageSize.value,
    search.value,
    storeFilter.value,
    sortOrder.value,
  ]),
  queryFn: async () => {
    const res = await FileService.searchFilesGestion({
      designation: search.value.trim() || undefined,
      restaurantIds: storeFilter.value ? [storeFilter.value] : undefined,
      page: page.value,
      pageSize: pageSize.value,
      sortOrder: sortOrder.value,
    });
    return res?.data;
  },
});
const filesErrorMessage = computed(() => {
  const err = filesErrorObj.value as any;
  const msgs = err?.response?.data?.message;
  return Array.isArray(msgs) ? msgs.join(' · ') : msgs || err?.message || '';
});

const files = computed<any[]>(() => filesData.value?.data ?? []);
const fileTotal = computed(() => filesData.value?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(fileTotal.value / pageSize.value)),
);
const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur = page.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});

function fileThumb(f: any) {
  return f?.info?.secureUrl ?? f?.info?.url ?? null;
}
function fileName(f: any) {
  return f?.designation || f?.info?.publicId || t('medias.untitled');
}
function fileStoreName(f: any) {
  return (
    f?.restaurant?.designation ??
    restaurantName(f?.restaurant?.id ?? f?.restaurantId)
  );
}

// ── Actions rapides (copier / voir) ────────────────────────────────────────────
const copiedFileId = ref<string | null>(null);

async function copyFileUrl(f: any) {
  const url = fileThumb(f);
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    copiedFileId.value = f.id;
    setTimeout(() => {
      if (copiedFileId.value === f.id) copiedFileId.value = null;
    }, 1500);
  } catch {
    // clipboard indisponible : ignoré silencieusement
  }
}
function viewFile(f: any) {
  const url = fileThumb(f);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

// ── Navigation détail / drawer upload ────────────────────────────────────────
const uploadDrawerOpen = ref(false);

function openDetailDrawer(file: any) {
  router.push(`/medias/${file.id}`);
}
function openUploadDrawer() {
  uploadDrawerOpen.value = true;
}
function closeUploadDrawer() {
  uploadDrawerOpen.value = false;
}
function onFileChanged() {
  queryClient.invalidateQueries({ queryKey: ['mp-files'] });
}
</script>

<template>
  <div class="mp" @click="storeDropdownOpen = false">
    <!-- Header -->
    <div class="mp__header">
      <div>
        <h1 class="mp__title">{{ t('medias.title') }}</h1>
        <p class="mp__subtitle">{{ t('medias.subtitle') }}</p>
      </div>
      <button class="mp__btn mp__btn--primary" @click="openUploadDrawer">
        <i class="pi pi-upload" /> {{ t('medias.uploadNew') }}
      </button>
    </div>

    <!-- Filters -->
    <div class="mp__filters">
      <div class="mp__search-wrap">
        <i class="pi pi-search mp__search-icon" />
        <input
          v-model="search"
          class="mp__search"
          :placeholder="t('medias.searchPlaceholder')"
        />
      </div>

      <div class="mp__dropdown-wrap" @click.stop>
        <button
          class="mp__dropdown-btn"
          :class="{ 'mp__dropdown-btn--active': storeFilter }"
          @click="storeDropdownOpen = !storeDropdownOpen"
        >
          <i class="pi pi-building" />
          <span>{{ selectedStoreName || t('medias.allStores') }}</span>
          <i v-if="storeFilter" class="pi pi-times" @click.stop="clearStore" />
          <i v-else class="pi pi-chevron-down" />
        </button>
        <Transition name="mp-drop">
          <div v-if="storeDropdownOpen" class="mp__dropdown-panel">
            <div class="mp__dropdown-search-wrap">
              <i class="pi pi-search" />
              <input
                v-model="storeSearch"
                class="mp__dropdown-search"
                :placeholder="t('medias.searchStore')"
                @click.stop
              />
            </div>
            <div class="mp__dropdown-list">
              <button
                class="mp__dropdown-item"
                :class="{ 'mp__dropdown-item--active': !storeFilter }"
                @click="clearStore"
              >
                {{ t('medias.allStores') }}
              </button>
              <button
                v-for="r in filteredStores"
                :key="r.id"
                class="mp__dropdown-item"
                :class="{ 'mp__dropdown-item--active': storeFilter === r.id }"
                @click="selectStore(r.id)"
              >
                {{ r.designation ?? r.name }}
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <select v-model.number="pageSize" class="mp__page-size-select">
        <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
          {{ ps }} {{ t('medias.perPage') }}
        </option>
      </select>

      <button
        class="mp__sort-btn"
        :title="t('medias.sortByDate')"
        @click="toggleSortOrder"
      >
        <i
          :class="
            sortOrder === 'DESC'
              ? 'pi pi-sort-amount-down'
              : 'pi pi-sort-amount-up'
          "
        />
        {{ t('medias.sortByDate') }}
      </button>

      <span class="mp__count">{{ fileTotal }} {{ t('medias.results') }}</span>
    </div>

    <!-- Grid -->
    <div v-if="filesLoading" class="mp__grid">
      <div v-for="i in 8" :key="i" class="mp__card mp__card--sk" />
    </div>
    <div v-else-if="filesError" class="mp__empty">
      <i class="pi pi-exclamation-circle" />
      <p>{{ filesErrorMessage || t('medias.loadError') }}</p>
    </div>
    <div v-else-if="!files.length" class="mp__empty">
      <i class="pi pi-images" />
      <p>{{ t('medias.empty') }}</p>
    </div>
    <div v-else class="mp__grid">
      <button
        v-for="f in files"
        :key="f.id"
        class="mp__card"
        @click="openDetailDrawer(f)"
      >
        <div class="mp__card-thumb">
          <img
            v-if="fileThumb(f)"
            :src="fileThumb(f)"
            :alt="fileName(f)"
            loading="lazy"
          />
          <i v-else class="pi pi-image" />
          <div class="mp__card-quick-actions">
            <button
              class="mp__quick-btn"
              :title="t('medias.view')"
              @click.stop="viewFile(f)"
            >
              <i class="pi pi-eye" />
            </button>
            <button
              class="mp__quick-btn"
              :title="t('medias.copyLink')"
              @click.stop="copyFileUrl(f)"
            >
              <i
                :class="copiedFileId === f.id ? 'pi pi-check' : 'pi pi-copy'"
              />
            </button>
          </div>
        </div>
        <div class="mp__card-body">
          <span class="mp__card-name">{{ fileName(f) }}</span>
          <span class="mp__card-store">
            <i class="pi pi-shop" /> {{ fileStoreName(f) }}
          </span>
        </div>
      </button>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mp__pagination">
      <button class="mp__pg-btn" :disabled="page === 1" @click="page = 1">
        <i class="pi pi-angle-double-left" />
      </button>
      <button class="mp__pg-btn" :disabled="page === 1" @click="page--">
        <i class="pi pi-chevron-left" />
      </button>
      <button
        v-for="p in visiblePages"
        :key="p"
        class="mp__pg-btn"
        :class="{ 'mp__pg-btn--active': p === page }"
        @click="page = p"
      >
        {{ p }}
      </button>
      <button
        class="mp__pg-btn"
        :disabled="page === totalPages"
        @click="page++"
      >
        <i class="pi pi-chevron-right" />
      </button>
      <button
        class="mp__pg-btn"
        :disabled="page === totalPages"
        @click="page = totalPages"
      >
        <i class="pi pi-angle-double-right" />
      </button>
    </div>

    <MediaDetailDrawer
      :open="uploadDrawerOpen"
      :file="null"
      :restaurants="restaurants"
      @close="closeUploadDrawer"
      @uploaded="onFileChanged"
    />
  </div>
</template>

<style scoped>
.mp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .mp {
    padding: 1.5rem;
  }
}

.mp__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.mp__title {
  margin: 0 0 0.15rem;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.mp__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}

.mp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.mp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.mp__btn--primary:hover {
  background: #e6c109;
}

/* Filters */
.mp__filters {
  position: relative;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 0.875rem 1rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.mp__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
}
.mp__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.mp__search {
  width: 100%;
  height: 2.5rem;
  padding: 0 0.875rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
}
.mp__search:focus {
  border-color: #ffd60a;
}

.mp__sort-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.mp__sort-btn:hover {
  border-color: #ffd60a;
}

.mp__dropdown-wrap {
  position: relative;
}
.mp__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.mp__dropdown-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.mp__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  min-width: 16rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.mp__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.mp__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.mp__dropdown-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
}
.mp__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.mp__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.mp__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}

.mp-drop-enter-active,
.mp-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.mp-drop-enter-from,
.mp-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.mp__page-size-select {
  height: 2.5rem;
  padding: 0 2rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.mp__count {
  margin-left: auto;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}

/* Grid & cards */
.mp__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}
.mp__card {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.875rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.mp__card:hover {
  border-color: rgba(255, 214, 10, 0.4);
}
.mp__card--sk {
  height: 12rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: mp-shimmer 1.4s infinite;
  cursor: default;
}
@keyframes mp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.mp__card-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-muted);
  font-size: 1.5rem;
}
.mp__card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.mp__card-quick-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.375rem;
  opacity: 0;
  transition: opacity 0.15s;
}
.mp__card-thumb:hover .mp__card-quick-actions {
  opacity: 1;
}
.mp__quick-btn {
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 50%;
  border: none;
  background: rgba(11, 19, 32, 0.65);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  cursor: pointer;
  backdrop-filter: blur(4px);
}
.mp__quick-btn:hover {
  background: rgba(11, 19, 32, 0.85);
}
.mp__card-body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.mp__card-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mp__card-store {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.mp__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

/* Pagination */
.mp__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}
.mp__pg-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.mp__pg-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
  font-weight: 700;
}
.mp__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
</style>

<style>
[data-theme='dark'] .mp__filters,
[data-theme='dark'] .mp__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mp__card:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .mp__search,
[data-theme='dark'] .mp__page-size-select,
[data-theme='dark'] .mp__sort-btn,
[data-theme='dark'] .mp__dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .mp__search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .mp__dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .mp__dropdown-item:hover,
[data-theme='dark'] .mp__dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mp__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .mp__card-thumb {
  background: rgba(255, 255, 255, 0.05);
}
</style>
